/**
 * @bash/shared
 *
 * Single source of truth for Bash types, schemas, errors, and utilities.
 *
 * @example
 * // Import types
 * import { Session, Message, ContentBlock } from '@bash/shared/types';
 *
 * // Import schemas for validation
 * import { UserMessagePayloadSchema, parseServerEnv } from '@bash/shared/schemas';
 *
 * // Import errors
 * import { BashError, ErrorCode } from '@bash/shared/errors';
 *
 * // Import constants
 * import { EVENTS } from '@bash/shared/constants';
 *
 * // Import utilities
 * import { createLogger, generateCorrelationId } from '@bash/shared/utils';
 */

// Re-export all types
export * from './types/index.js';

// Re-export the continue-anywhere journal marker (server ↔ runner, slice 1)
export * from './types/journal-continuity.js';

// Re-export the team contract (agents, first: spec 2026-09-19)
export * from './team/index.js';

// Re-export the app's destination registry (the composer knows the app, spec 2026-09-17)
export * from './app/index.js';
export * from './jev/index.js';

// Re-export all schemas
export * from './schemas/index.js';

// Re-export all errors
export * from './errors/index.js';

// Re-export all constants
export * from './constants/index.js';

// Re-export the event registry (single authored wire-contract table + derivations)
export * from './events/index.js';

// Re-export all utilities
export * from './utils/index.js';

// Re-export analytics constants
export * from './analytics/index.js';

// Re-export the waitlist's answers (the front door, spec 2026-10-17-waitlist-front-door)
export * from './waitlist/index.js';

// Re-export authz — plan-gated capability registry + policy
export * from './authz/index.js';

// Re-export content-models (model registry for content-creation skill)
export * from './content-models/index.js';

// Re-export content-engine (Studio MediaIntent + DB-driven registry/router contract)
export * from './content-engine/index.js';

// Re-export abilities module
export * from './abilities/index.js';

// Re-export items module (canonical item types + table maps)
export * from './items/index.js';

// Re-export Forge config-path resolver (.forge/ home)
export * from './config/index.js';

// Re-export harness module (swappable-harness spine contract)
export * from './harness/index.js';

// Re-export journal module (kernel append-only entry contract)
export * from './journal/index.js';

// Re-export memory import (parsers that bring other platforms' memories into the rail)
export * from './memory-import/index.js';

// P5.4 (Agent Teams) — delegated settle-marker detectors. Re-exported from
// @vestra/agent-sdk (kernel/interaction/strategy) so browser surfaces
// (apps/web) can parse the parent Agent tool_result's {status:'delegated'}
// marker without a dependency on @bash/kernel. Pure functions — safe for
// browser bundles (sourced from the agent-sdk pure root, not /engine).
export {
  DELEGATED_STATUS,
  isDelegatedResultContent,
  parseDelegatedResultContent,
} from '@vestra/agent-sdk';

// Re-export models module (provider-neutral model-capability registry)
export * from './models/index.js';

// Re-export composer module (conversational router contract)
export * from './composer/index.js';

// Bash's phone (spec 2026-10-14): numbers, calls, clips and the relay's tuning.
export * from './phone/index.js';

// The agent wallet (spec 2026-10-15): budgets, purchases, the rate table and every pinned sentence.
export * from './wallet/index.js';

// Tool search (T11): the hit record, its one renderer and the pre-search fence, for every surface that searches tools
export * from './tool-search/index.js';

// Re-export computer-use hands protocol (brain ↔ extension wire vocabulary)
export * from './computer-use/hands-protocol.js';

// Re-export computer-use device channel (sandbox ↔ server relay-lane vocabulary)
export * from './computer-use/device-channel.js';

// Re-export computer-use action report (actuator ground-truth response dialect)
export * from './computer-use/action-report.js';

// Re-export OS-hands protocol (brain ↔ VestraHands sidecar wire vocabulary)
export * from './computer-use/os-hands-protocol.js';

// Re-export the composer's step-lane RPC (server ↔ runner, T8d work_in_app)
export * from './computer-use/os-lane-protocol.js';

// Re-export the app recipes (T9 run_recipe): the recipe table and its server ↔ runner RPC
export * from './computer-use/os-recipes.js';
export * from './computer-use/os-recipe-coerce.js';
export * from './computer-use/os-recipe-lint.js';
export * from './computer-use/os-recipe-catalog.js';
export * from './computer-use/os-recipe-program.js';
export { APP_RECIPES, APP_RECIPES_DROPPED, SYSTEM_TOPIC_WORDS } from './computer-use/os-recipes/index.js';
export * from './computer-use/os-recipe-protocol.js';

// Re-export loopback control protocol (brain ↔ desktop shell wire vocabulary)
export * from './computer-use/loopback-protocol.js';

// Re-export OS-hands action report (sidecar ground-truth response dialect)
export * from './computer-use/os-action-report.js';

// Re-export Tier-0 verb registry (metadata SoT; executor bodies live in the Swift sidecar)
export * from './computer-use/os-verbs.js';

// Re-export the verb catalog search (scale plane: discovery for everything outside the hot set)
export * from './computer-use/os-verb-search.js';
export * from './computer-use/os-verb-inventory.js';

// Re-export the scale-plane constants (hot set + inline threshold, both lanes)
export * from './computer-use/os-verb-hotset.js';

// Re-export the early set + its inverses (the verbs a spoken partial may act on mid-sentence)
export * from './computer-use/os-verb-early-set.js';

// Re-export the bundle-id -> friendly app name helper (web card + server chat projection)
export * from './computer-use/friendly-app-name.js';

// Re-export the feature-flag registry (single authored FLAG_REGISTRY table + accessors)
export * from './flags/index.js';

// Re-export the conversation core (render-truth identity/order + narration —
// the pure reducers the web app and extension side panel both render from)
export * from './conversation/index.js';

// Re-export the home-briefing greeting corpus (tag-bucketed picker source)
export * from './home/index.js';

// Re-export the arrival routine prompt catalog (onboarding routine missions)
export * from './arrival/routine-prompts.js';

// Media-refs S2: the single MediaRef contract + ref.yaml serializer
export * from './media/media-ref.js';
export * from './media/file-id.js';
// The one video/audio/PDF classifier for the `media` block (video-understanding §4)
export * from './media/media-kind.js';
// The one thumbnail rule the server's ?thumb=1 link and the /v door share
export * from './media/image-row.js';
// The one creation name rule the web's cards and the server's share reads share
export * from './media/creation-title.js';

// Re-export the GTM Discovery popularity-per-dollar score (pure function)
export * from './gtm-discovery-score.js';

// Re-export the canonical product-vocabulary block (Vestra terms for the composer prompt)
export * from './product/lore.js';

// Re-export the Nami memory tree (the one memory/ path source + the citation atom)
export * from './nami/memory-tree.js';
export * from './nami/brain-worlds.js';
// The self-model: the six fixed pages about the user, their sections, and the
// Recent-updates row shape (Phase 4 Train 1).
export * from './nami/self-model.js';
export * from './nami/brain-page-ops.js';
export * from './nami/week-stamp.js';
// The sense rail's contract: one recipe describes a whole sense, and the
// validator is what stands between a user-written recipe and the payload.
export * from './nami/sense-recipe.js';
// The `[[…]]` grammars the apps speak — one home for both, reachable from the
// server and from a browser bundle. Pure string work, no dependencies.
export * from './nami/wikilink-scan.js';
export * from './nami/brain-select.js';
export * from './nami/eval-stats.js';
// "These two say the same thing" — the word-level rule the reconciler's fold and
// the Brain's collapse both read, so one brain cannot be folded two ways.
export * from './nami/learning-similarity.js';
// Nami decides when, Bash does the work: the one contract the prep and brief timers,
// the routine executor and the web all read (missions, payloads, the bound, the local clock).
export * from './nami/work-routines.js';

// Sandbox storage by lifetime: the one headroom sample the runner reports on the
// lease beat and the server stores (plus the tidy counters and the disk-full mark).
export * from './sandbox/vitals.js';
// Where every byte of ours lives on a box and how long it lives there: the one
// table the sweepers, the tidy pass and the mirror all read.
export * from './sandbox/lifetimes.js';
// The cold archive's wire contract: the bucket, the part size, the key shape and the bodies
// the runner's archiver and the server's broker exchange.
export * from './sandbox/archive.js';
// The sweeper's off | dry | on switch, parsed once for both halves (Ruling R80).
export * from './sandbox/tidy-mode.js';
// The disk guard: the volume's three lines (80 / 90 / 95%), its switch, and the state it rides the beat as.
export * from './sandbox/disk-guard.js';
// Work holds the box: the hold's off | shadow | act switch with its cohort narrowing,
// and the hold report the runner rides on every lease beat.
export * from './sandbox/hold-mode.js';
export * from './sandbox/hold.js';
// Hosts: where a user's box can run (box-hosts spec 2026-10-13 §1).
export * from './sandbox/hosts.js';
// A box feature that cannot reach the user's computer yet: one code, one sentence (box hosts, Plan 2c).
export * from './sandbox/not-on-this-computer.js';
// Tools on disk: one install-and-cache layout under a workspace root, for the server and the runner.
export * from './sandbox/tools-layout.js';
// The layout migration's report: the shape the runner's migrator builds on wake
// and the server validates, stores and relocates app records from.
export * from './sandbox/layout.js';
// A project under files/ is not documents: the static rule the runner's watcher asks,
// beside the lifetimes table's isLibraryFilePath.
export * from './sandbox/user-files-scope.js';
// Projects P1: the box's projects as the runner reports them on the lease beat, the one
// reader both halves use, and the row GET /api/projects answers.
export * from './sandbox/projects.js';
// L4c — the served MCP_SERVER_CONFIG bytes and their stable part (MCP_SCHEMA_CACHE_V2).
export * from './sandbox/mcp-served-config.js';
// Engines on the person's own plan: which engines, and where the person stands on each (spec 2026-09-28).
export * from './engines/standing.js';
// Several accounts per engine: the slot each account signs in to, and its row in Settings (spec 2026-10-01).
export * from './engines/accounts.js';
// The models a plan engine runs, by id, when its provider alone is too wide (Codex, engines T3).
export * from './engines/models.js';
